import { useEffect, useState, type JSX } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Link,
  Typography,
} from '@mui/material';
import { Svgs } from '../assets/svg';
import { useDeviceType } from '../hooks/useDeviceType';
import { insetBorder } from '../helpers/styleUtils';
import { ProductTypeEnum } from '../models/PricebookData';

export interface HelpChooseProductProps {
  recommendedProduct?: ProductTypeEnum;
  setRecommendedProduct: React.Dispatch<React.SetStateAction<ProductTypeEnum | undefined>>;
}

const HelpChooseProduct: React.FC<HelpChooseProductProps> = ({
  recommendedProduct,
  setRecommendedProduct,
}) => {
  const [selection, setSelection] = useState<number>();
  const [expanded, setExpanded] = useState<boolean>(false);
  const email = 'help@guidedchoice.com';

  const isSelected = (id: number) => {
    return selection === id;
  };

  const onSelect = (id: number) => {
    if (isSelected(id)) {
      setSelection(undefined);
      return;
    }
    setSelection(id);
  };

  const onChange = (event: React.SyntheticEvent, value: boolean) => {
    if (!value) {
      setSelection(undefined);
      setRecommendedProduct(undefined);
    }
    setExpanded(value);
  };

  const onEmail = () => {
    window.location.href = `mailto:${email}`;
  };

  const { isMobile, isTablet } = useDeviceType();

  useEffect(() => {
    const makeRecommendation = () => {
      switch (selection) {
        case 0:
        case 2:
          setRecommendedProduct(ProductTypeEnum.GuidedChoice);
          break;
        case 1:
        case 3:
          setRecommendedProduct(ProductTypeEnum.Nickels);
      }
    };

    makeRecommendation();
  }, [selection, setRecommendedProduct]);

  return (
    <Accordion expanded={expanded} onChange={onChange}>
      <AccordionSummary
        sx={{ 'paddingLeft': 0, '& > *': { fill: theme => theme.palette.secondary.main } }}
        expandIcon={expanded ? <Svgs.IconCollapse /> : <Svgs.IconAdd />}>
        <Typography variant='p18SemiBold' color='secondary'>
          Help me choose a product
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Card variant='ghost' color='primary'>
          <CardContent className='helpChoose'>
            <Typography variant='h2' color='secondary'>
              Which option best describes your needs?
            </Typography>

            <Box mt='20px'>
              {isMobile || isTablet ? (
                <Box display='flex' flexDirection='column' justifyContent='space-between'>
                  <Box display='flex' justifyContent='space-between'>
                    <ProductChoiceCard
                      boldText='my retired members have help tax-efficiently spending their savings'
                      art={<Svgs.ArtRetirement />}
                      id={0}
                      onSelect={onSelect}
                      isSelected={isSelected}
                    />
                    <ProductChoiceCard
                      boldText='we grow financial literacy in my organization'
                      art={<Svgs.ArtBooks />}
                      id={1}
                      onSelect={onSelect}
                      isSelected={isSelected}
                    />
                  </Box>
                  <Box display='flex' justifyContent='space-between' mt='20px'>
                    <ProductChoiceCard
                      boldText='my members have help with their retirement planning'
                      art={<Svgs.ArtTarget />}
                      id={2}
                      onSelect={onSelect}
                      isSelected={isSelected}
                    />
                    <ProductChoiceCard
                      boldText='my members have access to financial planning for their household'
                      art={<Svgs.ArtHouse />}
                      id={3}
                      onSelect={onSelect}
                      isSelected={isSelected}
                    />
                  </Box>
                </Box>
              ) : (
                <Box display='flex' justifyContent='space-between'>
                  <ProductChoiceCard
                    boldText='my retired members have help tax-efficiently spending their savings'
                    art={<Svgs.ArtRetirement />}
                    id={0}
                    onSelect={onSelect}
                    isSelected={isSelected}
                  />
                  <ProductChoiceCard
                    boldText='we grow financial literacy in my organization'
                    art={<Svgs.ArtBooks />}
                    id={1}
                    onSelect={onSelect}
                    isSelected={isSelected}
                  />
                  <ProductChoiceCard
                    boldText='my members have help with their retirement planning'
                    art={<Svgs.ArtTarget />}
                    id={2}
                    onSelect={onSelect}
                    isSelected={isSelected}
                  />
                  <ProductChoiceCard
                    boldText='my members have access to financial planning for their household'
                    art={<Svgs.ArtHouse />}
                    id={3}
                    onSelect={onSelect}
                    isSelected={isSelected}
                  />
                </Box>
              )}
            </Box>

            {recommendedProduct !== undefined && (
              <Box mt='30px' display='flex' flexDirection='column' gap='15px'>
                <Box display='flex' alignItems='center' gap='10px'>
                  <Svgs.ArtLightbulbSmall />
                  <Typography variant='p18SemiBold' color='secondary'>
                    Our recommendation:{' '}
                    {recommendedProduct === ProductTypeEnum.GuidedChoice
                      ? 'GuidedChoice'
                      : '3Nickels'}
                  </Typography>
                </Box>
                <Typography variant='body1' color='info.main'>
                  Based on your selection(s) above, we recommend{' '}
                  {recommendedProduct === ProductTypeEnum.GuidedChoice
                    ? 'GuidedChoice'
                    : '3Nickels'}{' '}
                  for your members.
                </Typography>
                <Box display='flex'>
                  <Typography variant='h3' color='primary'>
                    Still not sure? We're available M-F 8am-5pm PST at (800) 774-7459, or anytime
                    at&nbsp;
                  </Typography>
                  <Link
                    style={{ textDecoration: 'underline' }}
                    variant='h3'
                    color='primary'
                    onClick={onEmail}>
                    {email}
                  </Link>
                  <Typography variant='h2' color='primary'>
                    .
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </AccordionDetails>
    </Accordion>
  );
};

interface ProductChoiceCardProps {
  boldText: string;
  art: JSX.Element;
  id: number;
  onSelect: (id: number) => void;
  isSelected: (id: number) => boolean;
}

const ProductChoiceCard: React.FC<ProductChoiceCardProps> = ({
  boldText,
  art,
  id,
  onSelect,
  isSelected,
}) => {
  const selected = isSelected(id);
  const { isMobile, isTablet } = useDeviceType();
  return (
    <Card
      sx={theme => ({
        width: isMobile || isTablet ? '45%' : '22%',
        height: '160px',
        borderRadius: '10px',
        border: 'none',
        boxShadow: selected
          ? insetBorder(theme.palette.primary.light, 3)
          : insetBorder(theme.palette.info.main),
        position: 'relative',
      })}>
      <CardActionArea sx={{ height: '100%' }} onClick={() => onSelect(id)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
            padding: '15px 20px',
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              'height': '50px',
              'flexShrink': 0,
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'overflow': 'hidden',
              '& svg': { height: '100%', maxWidth: '60px', width: 'auto' },
            }}>
            {art}
          </Box>
          <Box mt='16px' />
          <Typography
            variant='p14'
            color='secondary.dark'
            textAlign='center'
            className='truncate-three-lines'>
            It&apos;s most important to me that{' '}
            <Typography component='span' variant='p14SemiBold' color='secondary.dark'>
              {boldText}
            </Typography>
          </Typography>
          {selected && (
            <Box sx={{ position: 'absolute', top: '15px', right: '15px' }}>
              <Svgs.IconCheckmarkCustomPrimary style={{ width: '30px', height: '30px' }} />
            </Box>
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default HelpChooseProduct;
